import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";
import type { TFunction } from "i18next";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

const TOOLTIP_DELAY_MS = 300;

/** Counts a hub group's scripts and briefs. Hovering breaks the count down. */
export function OpportunityHubScriptCountBadge({
  title,
  personaTitle,
  scriptCount,
  briefCount,
}: {
  title: string;
  personaTitle: string | null;
  scriptCount: number;
  briefCount: number;
}) {
  const { t } = useTranslation();

  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="slate"
            iconStart="file-text"
            className="text-[11px] tabular-nums"
            data-testid="opportunity-hub-script-count-badge"
          >
            {scriptGroupCountLabel(t, scriptCount, briefCount)}
          </Badge>
        </TooltipTrigger>
        <TooltipContent align="end" className="px-3 py-2">
          <div className="flex flex-col gap-1.5" data-testid="opportunity-hub-script-count-params">
            <p className="truncate font-medium">{title}</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              {personaTitle ? (
                <CountParam label={t("personaOpportunities.hub.scripts.countPersona", "Persona")}>
                  {personaTitle}
                </CountParam>
              ) : null}
              <CountParam label={t("personaOpportunities.hub.scripts.countScripts", "Scripts")}>
                {scriptCount}
              </CountParam>
              <CountParam label={t("personaOpportunities.hub.scripts.countBriefs", "Briefs")}>
                {briefCount}
              </CountParam>
            </dl>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function CountParam({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="opacity-70">{label}</dt>
      <dd className="tabular-nums">{children}</dd>
    </>
  );
}

function scriptGroupCountLabel(t: TFunction, scriptCount: number, briefCount: number): string {
  const scripts = t("personaOpportunities.scripts.groupCount", {
    defaultValue_one: "{{count}} script",
    defaultValue_other: "{{count}} scripts",
    count: scriptCount,
  });
  const briefs = t("personaOpportunities.scripts.briefs.groupCount", {
    defaultValue_one: "{{count}} brief",
    defaultValue_other: "{{count}} briefs",
    count: briefCount,
  });
  if (briefCount === 0) return scripts;
  if (scriptCount === 0) return briefs;
  return t("personaOpportunities.scripts.groupCountMixed", {
    defaultValue: "{{scripts}} · {{briefs}}",
    scripts,
    briefs,
  });
}
